import React, { useState, useEffect } from "react";
import "./InvoicePDFTemplate.scss";
import { getMyClientsByID } from "../../services/api/MyClientsApi";
import {
    getDistrictCasesByCourtRef,
    getHighCasesByCourtRef,
    getOtherCourtCasesAddedManuallyByCourtRef,
    getSupremeCourtCasesByCourtRef,
} from "../../services/api/CasesApi";

export const InvoicePDFTemplate = ({ invoice, settings }) => {
    const [clientDetails, setClientDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [courtRefMappings, setCourtRefMappings] = useState({
        highCourt: [],
        districtCourt: [],
        otherCourt: [],
        supremeCourt: [],
    });

    const {
        headerText,
        header,
        footer,
        displayHeaderFooter,
        displayBankDetails,
        bankDetails,
        isSignatureDigitalGenerated,
        digitalSignature,
        displayTermsAndConditions,
        termsAndConditions,
        gstNo,
        panNo,
        address,
    } = settings;

    // Add the getDisplayUrl function from InvoiceSettings
    const getDisplayUrl = (url) => {
        if (!url) return "";
        if (url.startsWith('http')) return url;
        return `${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${url}`;
    };

    // Fetch client details when component mounts
    useEffect(() => {
        const fetchClientDetails = async () => {
            if (invoice.issuedTo) {
                setLoading(true);
                try {
                    const response = await getMyClientsByID(invoice.issuedTo);
                    setClientDetails(response.data);
                } catch (error) {
                    console.error("Error fetching client details:", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchClientDetails();
    }, [invoice.issuedTo]);

    // Fetch court ref mappings if invoice has cases
    useEffect(() => {
        const fetchCourtRefMappings = async () => {
            const hasCases =
                (invoice.highCourtCases && invoice.highCourtCases.length > 0) ||
                (invoice.districtCourtCases && invoice.districtCourtCases.length > 0) ||
                (invoice.otherCourtCases && invoice.otherCourtCases.length > 0) ||
                (invoice.supremeCourtCases && invoice.supremeCourtCases.length > 0);

            if (!hasCases) return;

            try {
                const [highCourtRes, districtCourtRes, otherCourtRes, supremeCourtRes] = await Promise.all([
                    invoice.highCourtCases?.length > 0 ? getHighCasesByCourtRef() : Promise.resolve({ data: [] }),
                    invoice.districtCourtCases?.length > 0 ? getDistrictCasesByCourtRef() : Promise.resolve({ data: [] }),
                    invoice.otherCourtCases?.length > 0 ? getOtherCourtCasesAddedManuallyByCourtRef() : Promise.resolve({ data: [] }),
                    invoice.supremeCourtCases?.length > 0 ? getSupremeCourtCasesByCourtRef() : Promise.resolve({ data: [] }),
                ]);

                setCourtRefMappings({
                    highCourt: highCourtRes.data || [],
                    districtCourt: districtCourtRes.data || [],
                    otherCourt: otherCourtRes.data || [],
                    supremeCourt: supremeCourtRes.data || [],
                });
            } catch (error) {
                console.error("Error fetching court ref mappings:", error);
            }
        };

        fetchCourtRefMappings();
    }, [invoice.highCourtCases, invoice.districtCourtCases, invoice.otherCourtCases, invoice.supremeCourtCases]);

    // Helper function to get party name from court ref
    const getPartyNameByCourtRef = (courtRef, courtType) => {
        const mappings = courtRefMappings[courtType] || [];
        const found = mappings.find((m) => m.courtRef === courtRef);
        return found ? found.parties : null;
    };

    // Helper function to get caseTypeAndNumber from court ref
    const getCaseTypeAndNumberByCourtRef = (courtRef, courtType) => {
        const mappings = courtRefMappings[courtType] || [];
        const found = mappings.find((m) => m.courtRef === courtRef);
        return found?.caseTypeAndNumber || courtRef;
    };

    // Check if invoice has any associated cases
    const hasAssociatedCases =
        (invoice.highCourtCases && invoice.highCourtCases.length > 0) ||
        (invoice.districtCourtCases && invoice.districtCourtCases.length > 0) ||
        (invoice.otherCourtCases && invoice.otherCourtCases.length > 0) ||
        (invoice.supremeCourtCases && invoice.supremeCourtCases.length > 0);

    return (
        <div className="invoice-pdf">
            {displayHeaderFooter ? (
                header ? (
                    <img src={getDisplayUrl(header)} alt="Header" className="invoice-header" />
                ) : (
                    <p>Header not available</p>
                )
            ) : (
                <h2 className="company-name">{headerText}</h2>
            )}

            <div className="invoice-details-container req-pad">
                <div className="invoice-info">
                    <p><strong>Invoice No:</strong> {invoice.invoiceNumber}</p>
                    <p><strong>Date:</strong> {new Date(invoice.createdAt).toLocaleDateString()}</p>
                    {gstNo && <p><strong>GST No:</strong> {gstNo}</p>}
                    {panNo && <p><strong>PAN No:</strong> {panNo}</p>}
                    {address && <p><strong>Address:</strong> {address}</p>}
                    {hasAssociatedCases && (
                        <p><strong>Associated Case:</strong> {
                            [
                                ...(invoice.highCourtCases || []).map(courtRef => getCaseTypeAndNumberByCourtRef(courtRef, 'highCourt')),
                                ...(invoice.districtCourtCases || []).map(courtRef => getCaseTypeAndNumberByCourtRef(courtRef, 'districtCourt')),
                                ...(invoice.otherCourtCases || []).map(courtRef => getCaseTypeAndNumberByCourtRef(courtRef, 'otherCourt')),
                                ...(invoice.supremeCourtCases || []).map(courtRef => getCaseTypeAndNumberByCourtRef(courtRef, 'supremeCourt')),
                            ].join(', ')
                        }</p>
                    )}
                </div>

                <div className="client-info">
                    {loading ? (
                        <p>Loading client details...</p>
                    ) : clientDetails ? (
                        <>
                            <p><strong>Name:</strong> {clientDetails.salutation} {clientDetails.name}</p>
                            <p><strong>Company:</strong> {clientDetails.company}</p>
                            {clientDetails.gstin && <p><strong>GST No:</strong> {clientDetails.gstin}</p>}
                            {clientDetails.addressLine1 && <p><strong>Address:</strong> {clientDetails.addressLine1}</p>}
                            {clientDetails.addressLine2 && <p>{clientDetails.addressLine2}</p>}
                            <p>
                                {clientDetails.city}, {clientDetails.state} {clientDetails.zipcode && ` - ${clientDetails.zipcode}`}
                            </p>
                            <p>{clientDetails.country}</p>
                        </>
                    ) : (
                        <p>Client details not available</p>
                    )}
                </div>
            </div>

            <table className="invoice-table req-pad">
                <thead>
                    <tr>
                        <th>Services</th>
                        <th>Qty</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {invoice.items.map((item, i) => (
                        <tr key={i}>
                            <td>
                                {item.name}
                                {item.description && (
                                    <>
                                        <br />({item.description})
                                    </>
                                )}
                            </td>
                            <td>{item.qty}</td>
                            <td>₹{item.amount}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <table className="breakdown-table req-pad">
                <tbody>
                    <tr>
                        <td></td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>TOTAL</td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>Rs. {invoice.subtotal.toFixed(2)}</td>
                    </tr>

                    {gstNo && (
                        <>
                            {invoice.sgstPercentage > 0 && (
                                <tr>
                                    <td></td>
                                    <td style={{ textAlign: 'right' }}>SGST @ {invoice.sgstPercentage}%</td>
                                    <td style={{ textAlign: 'right' }}>Rs. {invoice.sgstAmount.toFixed(2)}</td>
                                </tr>
                            )}

                            {invoice.cgstPercentage > 0 && (
                                <tr>
                                    <td></td>
                                    <td style={{ textAlign: 'right' }}>CGST @ {invoice.cgstPercentage}%</td>
                                    <td style={{ textAlign: 'right' }}>Rs. {invoice.cgstAmount.toFixed(2)}</td>
                                </tr>
                            )}

                            {invoice.igstPercentage > 0 && (
                                <tr>
                                    <td></td>
                                    <td style={{ textAlign: 'right' }}>IGST @ {invoice.igstPercentage}%</td>
                                    <td style={{ textAlign: 'right' }}>Rs. {invoice.igstAmount.toFixed(2)}</td>
                                </tr>
                            )}
                        </>
                    )}
                    <tr style={{ backgroundColor: '#004A9A', color: 'white' }}>
                        <td></td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold', color: 'white' }}>PAYABLE AMOUNT</td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold', color: 'white' }}>
                            Rs. {gstNo
                                ? invoice.totalAmount.toFixed(2)
                                : invoice.subtotal.toFixed(2)}
                        </td>
                    </tr>
                </tbody>
            </table>

            {displayBankDetails && (
                <div className="bank-details req-pad">
                    <strong>Bank Details:</strong>
                    <p>{bankDetails}</p>
                </div>
            )}

            {/* Associated Cases Section */}
            {/* {hasAssociatedCases && (
                <div className="associated-cases req-pad">
                    <strong>Associated Cases:</strong>
                    
                    {invoice.highCourtCases && invoice.highCourtCases.length > 0 && (
                        <div className="case-type-section">
                            <p className="case-type-header">High Court Cases:</p>
                            <ul className="case-list">
                                {invoice.highCourtCases.map((courtRef, idx) => {
                                    const caseTypeAndNumber = getCaseTypeAndNumberByCourtRef(courtRef, 'highCourt');
                                    const parties = getPartyNameByCourtRef(courtRef, 'highCourt');
                                    return (
                                        <li key={idx}>
                                            <span className="case-type-number">{caseTypeAndNumber}</span>
                                            {parties && <span className="party-names"> - {parties}</span>}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {invoice.districtCourtCases && invoice.districtCourtCases.length > 0 && (
                        <div className="case-type-section">
                            <p className="case-type-header">District Court Cases:</p>
                            <ul className="case-list">
                                {invoice.districtCourtCases.map((courtRef, idx) => {
                                    const caseTypeAndNumber = getCaseTypeAndNumberByCourtRef(courtRef, 'districtCourt');
                                    const parties = getPartyNameByCourtRef(courtRef, 'districtCourt');
                                    return (
                                        <li key={idx}>
                                            <span className="case-type-number">{caseTypeAndNumber}</span>
                                            {parties && <span className="party-names"> - {parties}</span>}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {invoice.otherCourtCases && invoice.otherCourtCases.length > 0 && (
                        <div className="case-type-section">
                            <p className="case-type-header">Other Court Cases:</p>
                            <ul className="case-list">
                                {invoice.otherCourtCases.map((courtRef, idx) => {
                                    const caseTypeAndNumber = getCaseTypeAndNumberByCourtRef(courtRef, 'otherCourt');
                                    const parties = getPartyNameByCourtRef(courtRef, 'otherCourt');
                                    return (
                                        <li key={idx}>
                                            <span className="case-type-number">{caseTypeAndNumber}</span>
                                            {parties && <span className="party-names"> - {parties}</span>}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            )} */}

            {displayTermsAndConditions && termsAndConditions && (
                <div className="terms req-pad">
                    <strong>Terms & Conditions:</strong>
                    {termsAndConditions.split(/\d+\)\s?/).filter(Boolean).map((rule, idx) => (
                        <p key={idx}>{`${idx + 1}) ${rule}`}</p>
                    ))}
                </div>
            )}

            <div className="signature req-pad">
                {isSignatureDigitalGenerated && digitalSignature ? (
                    <img src={getDisplayUrl(digitalSignature)} alt="Digital Signature" className="signature-image" />
                ) : (
                    <p>This is a system-generated invoice</p>
                )}
            </div>

            {displayHeaderFooter && footer && (
                <img src={getDisplayUrl(footer)} alt="Footer" className="invoice-footer" />
            )}
        </div>
    );
};